import * as THREE from 'three';
import * as OBC from '@thatopen/components';
import * as OBF from '@thatopen/components-front';
import * as FRAGS from '@thatopen/fragments';
import { ViewHelper } from 'three/examples/jsm/helpers/ViewHelper.js';
// @ts-expect-error — raw-loader is a webpack plugin, no .d.ts shim for it
import workerSource from '!!raw-loader!./ifc-worker.js';
import { FragmentCache } from './FragmentCache';

export type ViewPreset = 'top' | 'front' | 'side' | 'iso';
export type ViewerTool = 'none' | 'length' | 'angle' | 'area' | 'clip';

export interface IfcViewerOptions {
    /** Render the OBC grid plane. Default: true. */
    showGrid?: boolean;
    /** Three-axis ViewHelper gizmo in the bottom-right corner. Default: true. */
    showGizmo?: boolean;
    /** Length/area/clipping tools (still need setTool() to activate). Default: true. */
    enableTools?: boolean;
    /** Hover highlight via raycaster on mousemove. Default: true. */
    hoverHighlight?: boolean;
}

/**
 * Framework-agnostic IFC renderer used by both the IFC importer page and the
 * DocumentManager record viewer. Wraps @thatopen/components + @thatopen/fragments,
 * exposes selection / isolation / view-preset primitives, and is fully disposable.
 *
 * Public surface is intentionally minimal — callers compose UI (tree, properties,
 * toolbars) on top via the <c>onElementSelected</c> / <c>onElementHovered</c>
 * callbacks and the <c>isolate</c> / <c>setView</c> / <c>setTool</c> methods.
 */
export class IfcViewerCore {
    private _components: OBC.Components;
    private _world: OBC.SimpleWorld<OBC.SimpleScene, OBC.SimpleCamera, OBF.PostproductionRenderer>;
    private _fragments: OBC.FragmentsManager;
    private _hider: OBC.Hider;
    private _bboxer: OBC.BoundingBoxer;
    private _highlighter: OBF.Highlighter;
    private _lengthMeasurement: OBF.LengthMeasurement | null = null;
    private _angleMeasurement: OBF.AngleMeasurement | null = null;
    private _areaMeasurement: OBF.AreaMeasurement | null = null;
    private _clipper: OBC.Clipper | null = null;
    private _viewHelper: ViewHelper | null = null;
    private _viewHelperClock: THREE.Clock | null = null;
    private _viewHelperClickHandler: ((e: PointerEvent) => void) | null = null;
    private _gizmoAnimating = false;
    private _gizmoRaf: number | null = null;
    private _container: HTMLElement | null = null;
    private _grid: { three: THREE.Object3D } | null = null;
    private _workerUrl: string;
    private _ready = false;
    private _model: FRAGS.FragmentsModel | null = null;
    private _modelId: string = '';
    private _suppressHighlightSelect = false;
    private _lastSelectionIdMap: OBC.ModelIdMap | null = null;
    private _ghostMode = false;
    private _dblclickHandler: ((e: MouseEvent) => void) | null = null;
    private readonly _options: Required<IfcViewerOptions>;

    onElementSelected: ((globalId: string | null) => void) | null = null;
    onElementHovered: ((globalId: string | null) => void) | null = null;

    constructor(options: IfcViewerOptions = {}) {
        this._options = {
            showGrid: options.showGrid ?? true,
            showGizmo: options.showGizmo ?? true,
            enableTools: options.enableTools ?? true,
            hoverHighlight: options.hoverHighlight ?? true,
        };
    }

    async mount(container: HTMLElement): Promise<void> {
        if (this._ready) return;
        this._container = container;

        this._components = new OBC.Components();
        const worlds = this._components.get(OBC.Worlds);
        this._world = worlds.create<
            OBC.SimpleScene,
            OBC.SimpleCamera,
            OBF.PostproductionRenderer
        >();

        this._world.scene = new OBC.SimpleScene(this._components);
        // PostproductionRenderer wraps the standard SimpleRenderer pipeline
        // and adds an EdgeDetectionPass we toggle from setWireframe(). Using
        // it from the start avoids the line-4330 crash you'd hit by setting
        // material.wireframe on fragments' custom InstancedBufferGeometries.
        this._world.renderer = new OBF.PostproductionRenderer(this._components, container);
        this._world.camera = new OBC.SimpleCamera(this._components);

        this._components.init();
        this._world.scene.setup();

        if (this._options.showGrid) {
            const grids = this._components.get(OBC.Grids);
            this._grid = grids.create(this._world);
        }

        const workerBlob = new Blob([workerSource], { type: 'text/javascript' });
        this._workerUrl = URL.createObjectURL(workerBlob);
        this._fragments = this._components.get(OBC.FragmentsManager);
        this._fragments.init(this._workerUrl);

        this._hider = this._components.get(OBC.Hider);
        this._bboxer = this._components.get(OBC.BoundingBoxer);

        const raycasters = this._components.get(OBC.Raycasters);
        raycasters.get(this._world);
        this._highlighter = this._components.get(OBF.Highlighter);
        this._highlighter.setup({
            world: this._world,
            selectMaterialDefinition: {
                color: new THREE.Color('#bcf124'),
                opacity: 1,
                transparent: false,
                renderedFaces: 0,
            },
        });
        this._highlighter.styles.set('hover', {
            color: new THREE.Color('#6528d7'),
            opacity: 0.6,
            transparent: true,
            renderedFaces: 0,
        });

        this._highlighter.events.select.onHighlight.add(async (modelIdMap: OBC.ModelIdMap) => {
            // Track the current selection so fitToSelection / setView /
            // gizmo-resync can read the element's bbox without us having to
            // re-resolve it elsewhere.
            this._lastSelectionIdMap = modelIdMap;
            if (this._suppressHighlightSelect || !this.onElementSelected) return;
            for (const [modelId, localIds] of Object.entries(modelIdMap)) {
                const model = this._fragments.list.get(modelId);
                if (!model) continue;
                const ids = Array.from(localIds);
                if (ids.length === 0) continue;
                const guids = await model.getGuidsByLocalIds(ids);
                const globalId = guids.find((g) => !!g);
                if (globalId) {
                    this.onElementSelected(globalId);
                    return;
                }
            }
        });

        this._highlighter.events.select.onClear.add(() => {
            this._lastSelectionIdMap = null;
            if (this._suppressHighlightSelect || !this.onElementSelected) return;
            this.onElementSelected(null);
        });

        if (this._options.hoverHighlight) {
            // Hover via raycaster on mousemove (RAF-throttled).
            let hoverRaf: number | null = null;
            let lastHoverGuid: string | null = null;
            container.addEventListener('mousemove', () => {
                if (hoverRaf !== null) return;
                hoverRaf = requestAnimationFrame(async () => {
                    hoverRaf = null;
                    try {
                        const raycaster = raycasters.get(this._world);
                        const result = await raycaster.castRay();
                        if (!result || !this._model) {
                            if (lastHoverGuid !== null) {
                                lastHoverGuid = null;
                                await this._highlighter.clear('hover').catch(() => {});
                                this.onElementHovered?.(null);
                            }
                            return;
                        }
                        const localId = (result as any).localId;
                        if (typeof localId !== 'number') return;
                        const guids = await this._model.getGuidsByLocalIds([localId]);
                        const guid = guids.find((g) => !!g) ?? null;
                        if (guid === lastHoverGuid) return;
                        lastHoverGuid = guid;
                        if (guid) {
                            const hlMap: OBC.ModelIdMap = {};
                            hlMap[this._modelId] = new Set([localId]);
                            await this._highlighter
                                .highlightByID('hover', hlMap, true, false)
                                .catch(() => {});
                            this.onElementHovered?.(guid);
                        } else {
                            await this._highlighter.clear('hover').catch(() => {});
                            this.onElementHovered?.(null);
                        }
                    } catch {}
                });
            });

            container.addEventListener('mouseleave', () => {
                lastHoverGuid = null;
                this._highlighter.clear('hover').catch(() => {});
                this.onElementHovered?.(null);
            });
        }

        if (this._options.enableTools) {
            // Tools are created up-front but disabled. setTool() flips them on/off.
            this._lengthMeasurement = this._components.get(OBF.LengthMeasurement);
            this._lengthMeasurement.world = this._world;
            this._lengthMeasurement.enabled = false;

            this._angleMeasurement = this._components.get(OBF.AngleMeasurement);
            this._angleMeasurement.world = this._world;
            this._angleMeasurement.enabled = false;

            this._areaMeasurement = this._components.get(OBF.AreaMeasurement);
            this._areaMeasurement.world = this._world;
            this._areaMeasurement.enabled = false;

            this._clipper = this._components.get(OBC.Clipper);
            this._clipper.enabled = false;

            this._dblclickHandler = (_e: MouseEvent) => {
                if (this._clipper?.enabled) {
                    this._clipper.create(this._world);
                } else if (this._lengthMeasurement?.enabled) {
                    this._lengthMeasurement.create();
                } else if (this._angleMeasurement?.enabled) {
                    this._angleMeasurement.create();
                } else if (this._areaMeasurement?.enabled) {
                    this._areaMeasurement.create();
                }
            };
            container.addEventListener('dblclick', this._dblclickHandler);
        }

        this._world.camera.controls.addEventListener('update', () => {
            this._fragments.core.update();
        });

        if (this._options.showGizmo) {
            // ViewHelper — click faces to snap to axis views.
            this._viewHelper = new ViewHelper(this._world.camera.three, container);
            this._viewHelper.setLabels('X', 'Y', 'Z');
            this._viewHelperClock = new THREE.Clock();

            // Render the gizmo overlay on every OBC frame (after the scene renders).
            this._world.renderer.onAfterUpdate.add(() => {
                if (!this._viewHelper || this._gizmoAnimating) return;
                const renderer = this._world.renderer.three;
                const prevAutoClear = renderer.autoClear;
                renderer.autoClear = false;
                renderer.clearDepth();
                this._viewHelper.render(renderer);
                renderer.autoClear = prevAutoClear;
            });

            this._viewHelperClickHandler = (e: PointerEvent) => {
                if (!this._viewHelper) return;
                // Only react inside the gizmo's bottom-right 128×128 corner.
                const rect = container.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                if (x < rect.width - 128 || y < rect.height - 128) return;

                const started = this._viewHelper.handleClick(e);
                if (started) this._startGizmoAnimation();
            };
            container.addEventListener('pointerup', this._viewHelperClickHandler);
        }

        this._fragments.list.onItemSet.add(async ({ value: model }) => {
            model.useCamera(this._world.camera.three);
            this._world.scene.three.add(model.object);
            this._model = model;
            this._modelId = model.modelId ?? '';
            this._fragments.core.update(true);
            await this.fitToAll();
            this.snapGridToEntryLevel();
        });

        // OBC.SimpleRenderer keeps an internal ResizeObserver on the
        // container and propagates size changes to the camera, so we don't
        // wire one up ourselves.

        this._ready = true;
    }

    /**
     * Snap the infinite grid to the bottom of the loaded model's bounding
     * box ("entry level"). Reuses the bboxer that <c>fitToAll</c> already
     * populated, so this method must be called AFTER fitToAll. Doesn't
     * depend on IfcBuildingStorey data being indexed — works for full
     * models, per-element sub-IFCs, and anything else with renderable
     * geometry.
     */
    private snapGridToEntryLevel(): void {
        if (!this._grid || !this._model) return;
        try {
            const bbox = this._bboxer.get();
            if (bbox.isEmpty()) return;
            this._grid.three.position.y = bbox.min.y;
        } catch {
            /* bboxer not ready — leave the grid at its default Y */
        }
    }

    /** Convenience: read a File and load it as IFC bytes. */
    async loadFile(file: File, onProgress?: (fraction: number) => void): Promise<void> {
        const arrayBuffer = await file.arrayBuffer();
        await this.loadBytes(new Uint8Array(arrayBuffer), 'ifc', onProgress);
    }

    /**
     * Load an IFC or pre-converted .frag binary into the viewer. The previous
     * model (if any) is fully released — including from FragmentsManager.list —
     * so reloading with the same modelId works (without disposeModel a second
     * load silently fails and the canvas stays blank).
     *
     * onProgress reports the IFC→fragments conversion fraction (0..1) — on big
     * models that conversion runs for MINUTES, and without live numbers the
     * page's loader looks hung.
     */
    async loadBytes(
        bytes: Uint8Array,
        kind: 'ifc' | 'frag' = 'ifc',
        onProgress?: (fraction: number) => void,
    ): Promise<void> {
        if (!this._ready) throw new Error('Viewer not mounted');

        if (this._model && this._modelId) {
            try {
                this._world.scene.three.remove(this._model.object);
            } catch {}
            try {
                await this._highlighter.clear('select');
            } catch {}
            try {
                await this._highlighter.clear('hover');
            } catch {}
            try {
                this._bboxer.dispose(false);
            } catch {}
            try {
                await this._fragments.core.disposeModel(this._modelId);
            } catch {}
            this._model = null;
            this._modelId = '';
            this._lastSelectionIdMap = null;
        }

        let fragmentBytes: Uint8Array;
        if (kind === 'frag') {
            fragmentBytes = bytes;
        } else {
            // The IFC→fragments conversion is the load's long pole (minutes of
            // single-threaded wasm tessellation on big models) and is
            // deterministic per input bytes — cache the converted fragments in
            // IndexedDB keyed by content hash, so re-opening the same file
            // (importer re-uploads, document-viewer re-opens) is instant.
            const cacheKey = await this.sha256Hex(bytes);
            const cached = await FragmentCache.get(cacheKey).catch(() => null);
            if (cached) {
                // eslint-disable-next-line no-console
                console.debug('[IFC] fragment cache hit — skipping conversion');
                onProgress?.(1);
                fragmentBytes = cached;
            } else {
                const serializer = new FRAGS.IfcImporter();
                // Serve web-ifc.wasm through the app's OWN static middleware
                // (/Static composites in dist/), which behaves identically on
                // Windows and Linux. The old `/dist/` path is served only by the
                // reverse-proxy/nginx layer, which on the Linux deployment does not
                // map it the same way — the wasm fetch then stalls and the "Loading
                // 3D preview... 95%" bar hangs forever.
                const wasmBase = `${window.location.origin}/Static/`;
                serializer.wasm = { absolute: true, path: wasmBase };
                // Fail FAST and CLEAR instead of hanging: verify the wasm is
                // actually reachable+typed before handing off to web-ifc (which has
                // no timeout of its own).
                await this.assertWasmReachable(`${wasmBase}web-ifc.wasm`);
                fragmentBytes = await serializer.process({
                    bytes,
                    progressCallback: onProgress ? (p) => onProgress(p) : undefined,
                });
                await FragmentCache.put(cacheKey, fragmentBytes).catch(() => {});
            }
        }

        // Post-conversion stages, each bounded: core.load is essential (a hang
        // here surfaces as a clear timeout error instead of an eternal loader);
        // update/fitToAll are cosmetic (bbox/framing) and must never hold the
        // loader hostage. Breadcrumbs let DevTools name a stalling stage.
        // eslint-disable-next-line no-console
        console.debug('[IFC] conversion done, loading fragments…');
        await this.withTimeout(
            this._fragments.core.load(fragmentBytes, { modelId: 'preview' }),
            120_000,
            'fragments core.load',
        );
        // Fast path: FragmentsModel.box is a METADATA-level bbox available
        // right after load — frame from it and return, letting geometry
        // stream in progressively on screen (the fragments engine's design).
        // Blocking on update(true) meant "Finalizing 3D…" held the loader for
        // the WHOLE geometry build — minutes on big models, and ~all of the
        // wait on fragment-cache hits.
        const metaBox = this._model?.box;
        if (metaBox && !metaBox.isEmpty()) {
            // eslint-disable-next-line no-console
            console.debug('[IFC] fragments loaded, framing from metadata bbox…');
            try {
                await this.withTimeout(this.frameBox(metaBox.clone()), 10_000, 'frame');
            } catch {}
            // Kick streaming eagerly, but off the critical path.
            this._fragments.core.update(true).catch(() => {});
            // eslint-disable-next-line no-console
            console.debug('[IFC] preview ready (geometry streams in)');
            return;
        }

        // Fallback (empty metadata box): wait for geometry, then frame.
        // eslint-disable-next-line no-console
        console.debug('[IFC] fragments loaded, updating geometry…');
        try {
            await this.withTimeout(this._fragments.core.update(true), 30_000, 'fragments update');
        } catch {}
        // eslint-disable-next-line no-console
        console.debug('[IFC] geometry updated, framing…');
        try {
            await this.withTimeout(this.fitToAll(), 10_000, 'fitToAll');
        } catch {}
        // eslint-disable-next-line no-console
        console.debug('[IFC] preview ready');
    }

    private async sha256Hex(bytes: Uint8Array): Promise<string> {
        const digest = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
        return Array.from(new Uint8Array(digest))
            .map((b) => b.toString(16).padStart(2, '0'))
            .join('');
    }

    private withTimeout<T>(promise: Promise<T>, ms: number, stage: string): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            const timer = setTimeout(
                () => reject(new Error(`3D preview stage "${stage}" timed out after ${ms / 1000}s`)),
                ms,
            );
            promise.then(
                (v) => { clearTimeout(timer); resolve(v); },
                (e) => { clearTimeout(timer); reject(e); },
            );
        });
    }

    // HEAD-probe the wasm before web-ifc streams it. A 404 (the Linux /dist
    // gap) or a wrong content-type (a proxy serving octet-stream breaks
    // WebAssembly.instantiateStreaming) throws a precise, actionable error
    // rather than leaving the preview stuck at 95%.
    private async assertWasmReachable(url: string): Promise<void> {
        let res: Response;
        try {
            res = await fetch(url, { method: 'HEAD', cache: 'no-store' });
        } catch (e) {
            throw new Error(`3D preview wasm unreachable at ${url}: ${(e as Error).message}`);
        }
        if (!res.ok) {
            throw new Error(`3D preview wasm not served (HTTP ${res.status}) at ${url}`);
        }
        const ct = res.headers.get('content-type') ?? '';
        if (!/wasm/i.test(ct)) {
            throw new Error(`3D preview wasm has wrong content-type "${ct}" at ${url} (expected application/wasm)`);
        }
    }

    async isolate(
        globalIds: string[],
        highlightFirst?: string,
        invert: boolean = false
    ): Promise<void> {
        if (!this._model) return;

        const ids = globalIds.filter((id) => !!id);
        if (ids.length === 0) {
            await this.showAll();
            return;
        }

        try {
            const localIds = await this._model.getLocalIdsByGuids(ids);
            const validIds = localIds.filter((id): id is number => id !== null);
            if (validIds.length === 0) {
                await this.showAll();
                return;
            }

            const idMap: OBC.ModelIdMap = {};
            idMap[this._modelId] = new Set(validIds);
            this._lastSelectionIdMap = idMap;

            if (invert) {
                await this._hider.set(false, idMap);
            } else if (this._ghostMode) {
                // Ghost mode: show all, just emphasize target via highlight.
                await this._hider.set(true);
            } else {
                await this._hider.isolate(idMap);
            }
            this._fragments.core.update(true);

            const guidToHighlight = highlightFirst || globalIds.find((id) => !!id);
            if (guidToHighlight) {
                const hlLocalIds = await this._model.getLocalIdsByGuids([guidToHighlight]);
                const hlValid = hlLocalIds.filter((id): id is number => id !== null);
                if (hlValid.length > 0) {
                    const hlMap: OBC.ModelIdMap = {};
                    hlMap[this._modelId] = new Set(hlValid);
                    this._suppressHighlightSelect = true;
                    try {
                        await this._highlighter.highlightByID('select', hlMap, true, false);
                    } finally {
                        setTimeout(() => {
                            this._suppressHighlightSelect = false;
                        }, 100);
                    }
                }
            }

            this._bboxer.dispose(false);
            await this._bboxer.addFromModelIdMap(idMap);
            await this.frameBox(this._bboxer.get());
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[IfcViewer] isolate failed:', err);
        }
    }

    async showAll(): Promise<void> {
        if (!this._model) return;
        try {
            this._suppressHighlightSelect = true;
            try {
                await this._highlighter.clear('select');
            } catch {}
            setTimeout(() => {
                this._suppressHighlightSelect = false;
            }, 100);

            await this._hider.set(true);
            this._fragments.core.update(true);
            this._lastSelectionIdMap = null;
            await this.fitToAll();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[IfcViewer] showAll failed:', err);
        }
    }

    async fitToSelection(): Promise<void> {
        if (this._lastSelectionIdMap) {
            this._bboxer.dispose(false);
            await this._bboxer.addFromModelIdMap(this._lastSelectionIdMap);
            await this.frameBox(this._bboxer.get());
        } else {
            await this.fitToAll();
        }
    }

    async setView(preset: ViewPreset): Promise<void> {
        if (!this._model) return;
        this._bboxer.dispose(false);
        if (this._lastSelectionIdMap) {
            await this._bboxer.addFromModelIdMap(this._lastSelectionIdMap);
        } else {
            this._bboxer.addFromModels();
        }
        const bbox = this._bboxer.get();
        if (bbox.isEmpty()) return;

        const center = bbox.getCenter(new THREE.Vector3());
        const size = bbox.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z) || 1;
        const dist = maxDim * 2.5;

        let eye: [number, number, number];
        switch (preset) {
            case 'top':
                eye = [center.x, center.y + dist, center.z + 0.001];
                break;
            case 'front':
                eye = [center.x, center.y, center.z + dist];
                break;
            case 'side':
                eye = [center.x + dist, center.y, center.z];
                break;
            case 'iso':
            default:
                eye = [center.x + dist * 0.6, center.y + dist * 0.5, center.z + dist * 0.6];
                break;
        }

        await this._world.camera.controls.setLookAt(
            eye[0],
            eye[1],
            eye[2],
            center.x,
            center.y,
            center.z,
            true
        );
    }

    async resetRotation(): Promise<void> {
        // Preserve distance from target, reset to iso orientation.
        const camera = this._world.camera;
        const target = new THREE.Vector3();
        camera.controls.getTarget(target);
        const position = new THREE.Vector3();
        camera.controls.getPosition(position);
        const dist = position.distanceTo(target) || 10;

        await camera.controls.setLookAt(
            target.x + dist * 0.6,
            target.y + dist * 0.5,
            target.z + dist * 0.6,
            target.x,
            target.y,
            target.z,
            true
        );
    }

    setGhostMode(enabled: boolean): void {
        this._ghostMode = enabled;
    }

    async hideByClass(ifcClasses: string[], hidden: boolean): Promise<void> {
        if (!this._model || ifcClasses.length === 0) return;
        try {
            const regexes = ifcClasses.map((c) => new RegExp(`^${c}$`, 'i'));
            const byCategory = await this._model.getItemsOfCategories(regexes);
            const allIds = new Set<number>();
            for (const cat in byCategory) {
                byCategory[cat].forEach((id) => allIds.add(id));
            }
            if (allIds.size === 0) return;
            const idMap: OBC.ModelIdMap = {};
            idMap[this._modelId] = allIds;
            await this._hider.set(!hidden, idMap);
            this._fragments.core.update(true);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[IfcViewer] hideByClass failed:', err);
        }
    }

    setTool(tool: ViewerTool): void {
        if (!this._options.enableTools) return;
        if (this._lengthMeasurement) this._lengthMeasurement.enabled = tool === 'length';
        if (this._angleMeasurement) this._angleMeasurement.enabled = tool === 'angle';
        if (this._areaMeasurement) this._areaMeasurement.enabled = tool === 'area';
        if (this._clipper) this._clipper.enabled = tool === 'clip';
    }

    clearMeasurements(): void {
        try {
            this._lengthMeasurement?.delete();
        } catch {}
        try {
            this._angleMeasurement?.delete();
        } catch {}
        try {
            this._areaMeasurement?.delete();
        } catch {}
        try {
            this._clipper?.deleteAll();
        } catch {}
    }

    // ── Escape hatches for advanced consumers (IfcEditor's transform tools,
    //    annotations) — these reach into the OBC layer directly. Returns null
    //    before mount() resolves.

    /** The OBC SimpleWorld (camera, scene, renderer). Use sparingly. */
    getWorld(): OBC.SimpleWorld<OBC.SimpleScene, OBC.SimpleCamera, OBF.PostproductionRenderer> | null {
        return this._world ?? null;
    }

    /** The OBC.Components container. Use to resolve services like OBF.Marker. */
    getComponents(): OBC.Components | null {
        return this._components ?? null;
    }

    /** The currently-loaded fragments model, for direct geometry access. */
    getCurrentModel(): FRAGS.FragmentsModel | null {
        return this._model;
    }

    private _wireframe = false;

    /**
     * Switch the renderer between full-shaded and "pen" (white-fill + black
     * silhouette / depth-discontinuity edges) styles via the
     * PostproductionRenderer. We deliberately don't touch material.wireframe —
     * fragments' InstancedBufferGeometry layout doesn't expose the index /
     * position attributes three's standard wireframe path needs, so flipping
     * the flag crashes inside three (line 4330). The postproduction edge
     * pass works on the depth/normal buffers instead, so it's compatible
     * with any geometry the fragments runtime emits.
     */
    setWireframe(enabled: boolean): void {
        this._wireframe = enabled;
        const post = this._world?.renderer?.postproduction;
        if (!post) return;
        post.enabled = enabled;
        if (enabled) {
            post.style = OBF.PostproductionAspect.PEN;
        }
    }

    toggleWireframe(): boolean {
        this.setWireframe(!this._wireframe);
        return this._wireframe;
    }

    /**
     * Highlight an element by GlobalId without changing visibility — used by
     * tree-pick where we want emphasis but no isolation. addToSelection=true
     * preserves any existing 'select' highlights.
     */
    async highlightByGlobalId(globalId: string, addToSelection: boolean = false): Promise<void> {
        if (!this._model || !globalId) return;
        try {
            const localIds = await this._model.getLocalIdsByGuids([globalId]);
            const validIds = localIds.filter((id): id is number => id !== null);
            if (validIds.length === 0) return;
            const map: OBC.ModelIdMap = {};
            map[this._modelId] = new Set(validIds);
            this._suppressHighlightSelect = true;
            try {
                await this._highlighter.highlightByID('select', map, true, addToSelection);
            } finally {
                setTimeout(() => {
                    this._suppressHighlightSelect = false;
                }, 100);
            }
        } catch {
            /* element not in fragments — silently ignore */
        }
    }

    private _startGizmoAnimation(): void {
        if (!this._viewHelper || !this._viewHelperClock) return;
        if (this._gizmoAnimating) return;

        // Freeze camera-controls while ViewHelper is writing to camera.position /
        // quaternion. Otherwise camera-controls overwrites them every frame and
        // the gizmo motion looks broken.
        const controls = this._world.camera.controls as any;
        const wasEnabled = controls.enabled !== false;
        if (wasEnabled) controls.enabled = false;

        this._gizmoAnimating = true;
        this._viewHelperClock.start();

        const step = () => {
            if (!this._viewHelper || !this._viewHelperClock) {
                this._gizmoAnimating = false;
                return;
            }

            const delta = this._viewHelperClock.getDelta();
            this._viewHelper.update(delta);

            // Render: scene first, gizmo overlay on top.
            if (!this._world.renderer) return;
            const renderer = this._world.renderer.three;
            const scene = this._world.scene.three;
            const camera = this._world.camera.three;
            renderer.render(scene, camera);
            const prevAutoClear = renderer.autoClear;
            renderer.autoClear = false;
            renderer.clearDepth();
            this._viewHelper.render(renderer);
            renderer.autoClear = prevAutoClear;

            if (this._viewHelper.animating) {
                this._gizmoRaf = requestAnimationFrame(step);
            } else {
                this._gizmoRaf = null;
                this._gizmoAnimating = false;

                // Sync camera-controls' target/position to the camera's final
                // orientation so subsequent orbit/zoom/pan resumes from where
                // the gizmo landed.
                try {
                    this._bboxer.dispose(false);
                    if (this._lastSelectionIdMap) {
                        this._bboxer.addFromModelIdMap(this._lastSelectionIdMap);
                    } else if (this._model) {
                        this._bboxer.addFromModels();
                    }
                    const bbox = this._bboxer.get();
                    const target = bbox.isEmpty()
                        ? new THREE.Vector3()
                        : bbox.getCenter(new THREE.Vector3());
                    const pos = camera.position.clone();
                    controls.setLookAt(pos.x, pos.y, pos.z, target.x, target.y, target.z, false);
                } catch {
                    /* non-fatal */
                }

                if (wasEnabled) controls.enabled = true;
                this._fragments.core.update(true);
            }
        };

        this._gizmoRaf = requestAnimationFrame(step);
    }

    private async fitToAll(): Promise<void> {
        if (!this._model) return;
        this._bboxer.dispose(false);
        this._bboxer.addFromModels();
        await this.frameBox(this._bboxer.get());
    }

    private async frameBox(bbox: THREE.Box3): Promise<void> {
        if (bbox.isEmpty()) return;
        const center = bbox.getCenter(new THREE.Vector3());
        const size = bbox.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z) || 1;
        const dist = maxDim * 2.5;
        await this._world.camera.controls.setLookAt(
            center.x + dist * 0.6,
            center.y + dist * 0.5,
            center.z + dist * 0.6,
            center.x,
            center.y,
            center.z,
            true
        );
    }

    dispose(): void {
        if (this._container && this._dblclickHandler) {
            this._container.removeEventListener('dblclick', this._dblclickHandler);
            this._dblclickHandler = null;
        }
        if (this._container && this._viewHelperClickHandler) {
            this._container.removeEventListener('pointerup', this._viewHelperClickHandler);
            this._viewHelperClickHandler = null;
        }
        if (this._gizmoRaf !== null) {
            cancelAnimationFrame(this._gizmoRaf);
            this._gizmoRaf = null;
        }
        if (this._viewHelper) {
            try {
                this._viewHelper.dispose();
            } catch {}
            this._viewHelper = null;
        }
        this._viewHelperClock = null;
        this._gizmoAnimating = false;
        if (this._workerUrl) URL.revokeObjectURL(this._workerUrl);
        try {
            this._components?.dispose();
        } catch {}
        this._container = null;
        this._ready = false;
    }
}
